import { DEFAULT_RIBBON_HALFTONE_MATERIAL, SUBTLE_RIBBON_INTERACTION } from "../interaction";
import { HORIZONTAL_ENDCAP_STATES, endcapSteps } from "./endcap";
import { CHAPTER_ENTRY_END, CHAPTER_ENTRY_START, CHAPTER_MEDIA_END, CHAPTER_MEDIA_START, FINAL_CHAPTER_MEDIA_END, LANDING_CURVE_DISPLACEMENT_SCALE, LANDING_CURVE_FAN_SPREAD, LANDING_CURVE_HORIZONTAL_STRETCH, LANDING_CURVE_TWIST_SPREAD, LANDING_ENDCAP_ZOOM, LANDING_HERO_ZOOM, LANDING_MOBILE_CURVE_FAN_SPREAD, LANDING_MOBILE_CURVE_HORIZONTAL_STRETCH, LANDING_MOBILE_CURVE_TWIST_SPREAD, LANDING_MOBILE_HERO_ZOOM, LANDING_MOBILE_LINE_EXTENT, LANDING_VERTICAL_WIDTH_SCALE, LINE_SPACING, MATCHED_IDLE_SPEED, MOBILE_OPEN_FAN_SPREAD, MOBILE_OPEN_HORIZONTAL_STRETCH, MOBILE_OPEN_TWIST_SPREAD, OPEN_FAN_SPREAD, OPEN_HORIZONTAL_STRETCH, OPEN_TURBULENCE, OPEN_TWIST_RETENTION, OPEN_TWIST_SPREAD, OPEN_UNRAVEL, PAPER, REVEAL_SPACING, STANDARD_END, STANDARD_START, VERTICAL_ROTATION, } from "./shared";
const LANDING_CURVE_HEADED_HOLD_VALUES = {
    background: PAPER,
    zoom: 1.2,
    ribbonWidthScale: LANDING_VERTICAL_WIDTH_SCALE,
    halftoneApparentZoom: LANDING_ENDCAP_ZOOM,
    center: [0.5, 0.5],
    rotationDeg: VERTICAL_ROTATION,
    lineExtent: 0.452,
    turbulence: OPEN_TURBULENCE,
    spacing: REVEAL_SPACING,
    speed: MATCHED_IDLE_SPEED,
    dither: 0.8,
    spatialColor: 0.54,
    twistRetention: OPEN_TWIST_RETENTION,
    fanSpread: LANDING_CURVE_FAN_SPREAD,
    twistSpread: LANDING_CURVE_TWIST_SPREAD,
    horizontalStretch: LANDING_CURVE_HORIZONTAL_STRETCH,
    unravel: OPEN_UNRAVEL,
    curveStrength: 1,
    curveDisplacementScale: LANDING_CURVE_DISPLACEMENT_SCALE,
    curveProgress: 0.95,
    mobile: {
        zoom: 0.98,
        halftoneApparentZoom: LANDING_ENDCAP_ZOOM,
        center: [0.5, 0.5],
        fanSpread: LANDING_MOBILE_CURVE_FAN_SPREAD,
        twistSpread: LANDING_MOBILE_CURVE_TWIST_SPREAD,
        horizontalStretch: LANDING_MOBILE_CURVE_HORIZONTAL_STRETCH,
    },
};
/**
 * Landing: a vertical strand that floods open, curves through numbered
 * chapters and hands off horizontally at the endcap. States are verbatim from
 * the prototype's LANDING_AURORA_CONFIG; the timeline is expressed as
 * role-driven step templates instead of selector-bound entries.
 */
export const LANDING_PRESET = {
    id: "landing",
    roles: ["flood", "chapter", "endcap"],
    mobileBreakpoint: 900,
    palette: "primary-v2-landing-blue",
    shape: "line",
    halftone: DEFAULT_RIBBON_HALFTONE_MATERIAL,
    interaction: SUBTLE_RIBBON_INTERACTION,
    curveProfile: {
        viewportSpan: 0.45,
        xCheckpoints: [0.2, 0.82, 0.56],
        bendWindows: [0.08, 0.36, 0.62, 0.9],
        startWidthScale: 0.5,
        mobile: {
            viewportSpan: 0.38,
            xCheckpoints: [0.28, 0.74, 0.56],
            startWidthScale: 1,
        },
    },
    initialStateId: "hero",
    states: [
        {
            id: "hero",
            background: PAPER,
            zoom: LANDING_HERO_ZOOM,
            ribbonWidthScale: LANDING_VERTICAL_WIDTH_SCALE,
            center: [0.5016, 0.4918],
            rotationDeg: VERTICAL_ROTATION,
            lineExtent: 0.452,
            turbulence: 0.37,
            spacing: LINE_SPACING,
            speed: MATCHED_IDLE_SPEED,
            dither: 1,
            spatialColor: 0.04,
            twistRetention: 1,
            fanSpread: 0,
            twistSpread: 0,
            horizontalStretch: 1,
            unravel: 0,
            curveStrength: 0,
            curveDisplacementScale: LANDING_CURVE_DISPLACEMENT_SCALE,
            curveProgress: 0,
            mobile: {
                zoom: LANDING_MOBILE_HERO_ZOOM,
                lineExtent: LANDING_MOBILE_LINE_EXTENT,
            },
        },
        {
            id: "flood",
            background: PAPER,
            zoom: LANDING_HERO_ZOOM,
            ribbonWidthScale: LANDING_VERTICAL_WIDTH_SCALE,
            center: [0.5, 0.5],
            rotationDeg: VERTICAL_ROTATION,
            lineExtent: 0.452,
            turbulence: OPEN_TURBULENCE,
            spacing: REVEAL_SPACING,
            speed: MATCHED_IDLE_SPEED,
            dither: 1,
            spatialColor: 0.12,
            twistRetention: OPEN_TWIST_RETENTION,
            fanSpread: OPEN_FAN_SPREAD,
            twistSpread: OPEN_TWIST_SPREAD,
            horizontalStretch: OPEN_HORIZONTAL_STRETCH,
            unravel: OPEN_UNRAVEL,
            curveStrength: 0,
            curveDisplacementScale: LANDING_CURVE_DISPLACEMENT_SCALE,
            curveProgress: 0,
            mobile: {
                zoom: LANDING_MOBILE_HERO_ZOOM,
                center: [0.5, 0.5],
                lineExtent: LANDING_MOBILE_LINE_EXTENT,
                fanSpread: MOBILE_OPEN_FAN_SPREAD,
                twistSpread: MOBILE_OPEN_TWIST_SPREAD,
                horizontalStretch: MOBILE_OPEN_HORIZONTAL_STRETCH,
            },
        },
        {
            id: "curve-story-entry",
            background: PAPER,
            zoom: 1.45,
            ribbonWidthScale: LANDING_VERTICAL_WIDTH_SCALE,
            halftoneApparentZoom: LANDING_ENDCAP_ZOOM,
            center: [0.5, 0.5],
            rotationDeg: VERTICAL_ROTATION,
            lineExtent: 0.452,
            turbulence: OPEN_TURBULENCE,
            spacing: REVEAL_SPACING,
            speed: MATCHED_IDLE_SPEED,
            dither: 1,
            spatialColor: 0.34,
            twistRetention: OPEN_TWIST_RETENTION,
            fanSpread: LANDING_CURVE_FAN_SPREAD,
            twistSpread: LANDING_CURVE_TWIST_SPREAD,
            horizontalStretch: LANDING_CURVE_HORIZONTAL_STRETCH,
            unravel: OPEN_UNRAVEL,
            curveStrength: 1,
            curveDisplacementScale: LANDING_CURVE_DISPLACEMENT_SCALE,
            curveProgress: 0.18,
            mobile: {
                zoom: 1.1,
                halftoneApparentZoom: LANDING_ENDCAP_ZOOM,
                center: [0.5, 0.5],
                fanSpread: LANDING_MOBILE_CURVE_FAN_SPREAD,
                twistSpread: LANDING_MOBILE_CURVE_TWIST_SPREAD,
                horizontalStretch: LANDING_MOBILE_CURVE_HORIZONTAL_STRETCH,
            },
        },
        {
            id: "curve-story-hold",
            background: PAPER,
            zoom: 1.32,
            ribbonWidthScale: LANDING_VERTICAL_WIDTH_SCALE,
            halftoneApparentZoom: LANDING_ENDCAP_ZOOM,
            center: [0.5, 0.5],
            rotationDeg: VERTICAL_ROTATION,
            lineExtent: 0.452,
            turbulence: OPEN_TURBULENCE,
            spacing: REVEAL_SPACING,
            speed: MATCHED_IDLE_SPEED,
            dither: 1,
            spatialColor: 0.38,
            twistRetention: OPEN_TWIST_RETENTION,
            fanSpread: LANDING_CURVE_FAN_SPREAD,
            twistSpread: LANDING_CURVE_TWIST_SPREAD,
            horizontalStretch: LANDING_CURVE_HORIZONTAL_STRETCH,
            unravel: OPEN_UNRAVEL,
            curveStrength: 1,
            curveDisplacementScale: LANDING_CURVE_DISPLACEMENT_SCALE,
            curveProgress: 0.32,
            mobile: {
                zoom: 1.02,
                halftoneApparentZoom: LANDING_ENDCAP_ZOOM,
                center: [0.5, 0.5],
                fanSpread: LANDING_MOBILE_CURVE_FAN_SPREAD,
                twistSpread: LANDING_MOBILE_CURVE_TWIST_SPREAD,
                horizontalStretch: LANDING_MOBILE_CURVE_HORIZONTAL_STRETCH,
            },
        },
        {
            id: "curve-program-entry",
            background: PAPER,
            zoom: 1.28,
            ribbonWidthScale: LANDING_VERTICAL_WIDTH_SCALE,
            halftoneApparentZoom: LANDING_ENDCAP_ZOOM,
            center: [0.5, 0.5],
            rotationDeg: VERTICAL_ROTATION,
            lineExtent: 0.452,
            turbulence: OPEN_TURBULENCE,
            spacing: REVEAL_SPACING,
            speed: MATCHED_IDLE_SPEED,
            dither: 1,
            spatialColor: 0.42,
            twistRetention: OPEN_TWIST_RETENTION,
            fanSpread: LANDING_CURVE_FAN_SPREAD,
            twistSpread: LANDING_CURVE_TWIST_SPREAD,
            horizontalStretch: LANDING_CURVE_HORIZONTAL_STRETCH,
            unravel: OPEN_UNRAVEL,
            curveStrength: 1,
            curveDisplacementScale: LANDING_CURVE_DISPLACEMENT_SCALE,
            curveProgress: 0.44,
            mobile: {
                zoom: 1,
                halftoneApparentZoom: LANDING_ENDCAP_ZOOM,
                center: [0.5, 0.5],
                fanSpread: LANDING_MOBILE_CURVE_FAN_SPREAD,
                twistSpread: LANDING_MOBILE_CURVE_TWIST_SPREAD,
                horizontalStretch: LANDING_MOBILE_CURVE_HORIZONTAL_STRETCH,
            },
        },
        {
            id: "curve-program-hold",
            background: PAPER,
            zoom: 1.18,
            ribbonWidthScale: LANDING_VERTICAL_WIDTH_SCALE,
            halftoneApparentZoom: LANDING_ENDCAP_ZOOM,
            center: [0.5, 0.5],
            rotationDeg: VERTICAL_ROTATION,
            lineExtent: 0.452,
            turbulence: OPEN_TURBULENCE,
            spacing: REVEAL_SPACING,
            speed: MATCHED_IDLE_SPEED,
            dither: 1,
            spatialColor: 0.46,
            twistRetention: OPEN_TWIST_RETENTION,
            fanSpread: LANDING_CURVE_FAN_SPREAD,
            twistSpread: LANDING_CURVE_TWIST_SPREAD,
            horizontalStretch: LANDING_CURVE_HORIZONTAL_STRETCH,
            unravel: OPEN_UNRAVEL,
            curveStrength: 1,
            curveDisplacementScale: LANDING_CURVE_DISPLACEMENT_SCALE,
            curveProgress: 0.6,
            mobile: {
                zoom: 0.96,
                halftoneApparentZoom: LANDING_ENDCAP_ZOOM,
                center: [0.5, 0.5],
                fanSpread: LANDING_MOBILE_CURVE_FAN_SPREAD,
                twistSpread: LANDING_MOBILE_CURVE_TWIST_SPREAD,
                horizontalStretch: LANDING_MOBILE_CURVE_HORIZONTAL_STRETCH,
            },
        },
        {
            id: "curve-headed-entry",
            background: PAPER,
            zoom: 1.35,
            ribbonWidthScale: LANDING_VERTICAL_WIDTH_SCALE,
            halftoneApparentZoom: LANDING_ENDCAP_ZOOM,
            center: [0.5, 0.5],
            rotationDeg: VERTICAL_ROTATION,
            lineExtent: 0.452,
            turbulence: OPEN_TURBULENCE,
            spacing: REVEAL_SPACING,
            speed: MATCHED_IDLE_SPEED,
            dither: 1,
            spatialColor: 0.5,
            twistRetention: OPEN_TWIST_RETENTION,
            fanSpread: LANDING_CURVE_FAN_SPREAD,
            twistSpread: LANDING_CURVE_TWIST_SPREAD,
            horizontalStretch: LANDING_CURVE_HORIZONTAL_STRETCH,
            unravel: OPEN_UNRAVEL,
            curveStrength: 1,
            curveDisplacementScale: LANDING_CURVE_DISPLACEMENT_SCALE,
            curveProgress: 0.78,
            mobile: {
                zoom: 1.08,
                halftoneApparentZoom: LANDING_ENDCAP_ZOOM,
                center: [0.5, 0.5],
                fanSpread: LANDING_MOBILE_CURVE_FAN_SPREAD,
                twistSpread: LANDING_MOBILE_CURVE_TWIST_SPREAD,
                horizontalStretch: LANDING_MOBILE_CURVE_HORIZONTAL_STRETCH,
            },
        },
        {
            id: "curve-headed-hold",
            ...LANDING_CURVE_HEADED_HOLD_VALUES,
        },
        {
            id: "curve-headed-hidden",
            ...LANDING_CURVE_HEADED_HOLD_VALUES,
            effectOpacity: 0,
        },
        ...HORIZONTAL_ENDCAP_STATES,
    ],
    chapterTables: {
        entry: ["curve-story-entry", "curve-program-entry", "curve-headed-entry"],
        hold: ["curve-story-hold", "curve-program-hold", "curve-headed-hold"],
    },
    steps: [
        { role: "flood", stateId: "flood", start: STANDARD_START, end: STANDARD_END },
        {
            role: "chapter",
            chapterPhase: "entry",
            start: CHAPTER_ENTRY_START,
            end: CHAPTER_ENTRY_END,
        },
        {
            role: "chapter",
            chapterPhase: "hold",
            start: CHAPTER_MEDIA_START,
            end: CHAPTER_MEDIA_END,
            finalEnd: FINAL_CHAPTER_MEDIA_END,
        },
        ...endcapSteps("curve-headed-hidden"),
    ],
};
//# sourceMappingURL=landing.js.map